import Vue from "vue";
import VueRouter from "vue-router";
import Dashboard from "@/views/Dashboard.vue";
import Agencies from "@/views/agencies/Agencies.vue";
import AgencyDetails from "@/views/agencies/components/AgencyDetails.vue";
import PageNotFound from "@/views/PageNotFound.vue";
import AccessDenied from "@/views/AccessDenied.vue";
import { isPermissioned } from "@/utils/permissions";
import { NS_FETCH_SELF_DETAILS } from "@/store/modules/auth/action-types";
// import { SET_AUTH_DIALOG_VISIBILITY } from "@/store/modules/global/mutation-types"; used for embedded login
import store from "@/store";
import { MAIN_NAVIGATION_ITEMS } from "@/router/constants";
import { settingsRouter } from "@/router/settings-router";
import { bookingsRouter } from "@/router/bookings-router";
import { offersRouter } from "@/router/offers-router";
import { conversationsRoutes } from "@/router/conversations-router";
import { interviewsRouter } from "@/router/interviews-router";
import { applicationsRouter } from "@/router/applications-router";
import { workersRouter } from "@/router/workers-router";
import { reportsRouter } from "@/router/reports-router";
import { profileRouter } from "@/router/profile-router";
import { getToken } from "@/services/utils";
import { get, includes } from "lodash";
import { USER_STATUSES } from "@/constants/users";
import ApplicationSubmitted from "@/views/bookings/ApplicationSubmitted/ApplicationSubmitted";
import ProfileSubmitted from "@/views/bookings/ProfileSubmitted/ProfileSubmitted";
import { getAuthLoginUrl } from "@/services/APIService";
import Exports from "@/views/exports/ExportTabs.vue";

// WIP
// import ProfileSubmitted from "@/views/bookings/ProfileSubmitted/ProfileSubmitted";

const { DASHBOARD, AGENCIES, EXPORTS } = MAIN_NAVIGATION_ITEMS;

Vue.use(VueRouter);

export const redirectWithPermissionCheck = (next, permission) => {
  if (!isPermissioned(permission)) {
    next({ name: "accessDenied" });
    throw new Error("You don't have permission to view this page");
  }
  return next();
};

["push", "replace"].forEach(method => {
  const originalMethod = VueRouter.prototype[method];
  VueRouter.prototype[method] = function m(location) {
    return originalMethod.call(this, location).catch(error => {
      if (error && error.name !== "NavigationDuplicated") {
        console.error(error);
      }
    });
  };
});

const getFirstPermissionedRouteName = () => {
  const names = Object.values(MAIN_NAVIGATION_ITEMS);
  for (let name of names) {
    if (isPermissioned(name)) {
      return name;
    }
  }
  return "accessDenied";
};

const checkForNewToken = token => {
  if (token) {
    localStorage.setItem("token", token);
    localStorage.removeItem("selectedProfile");
  }
  return token;
};

const routes = [
  {
    path: "*",
    name: "pageNotFound",
    component: PageNotFound
  },
  {
    path: "/access-denied",
    name: "accessDenied",
    component: AccessDenied
  },
  {
    path: "/dashboard",
    name: DASHBOARD,
    component: Dashboard,
    beforeEnter: (to, from, next) => {
      return redirectWithPermissionCheck(next, DASHBOARD);
    }
  },
  {
    path: "/agencies",
    name: AGENCIES,
    component: Agencies,
    beforeEnter: (to, from, next) => {
      return redirectWithPermissionCheck(next, AGENCIES);
    }
  },
  {
    path: "/agencies/:id",
    name: "agencyDetails",
    component: AgencyDetails,
    beforeEnter: (to, from, next) => {
      return redirectWithPermissionCheck(next, "agencies.view");
    }
  },
  {
    path: "/exports",
    name: EXPORTS,
    component: Exports,
    beforeEnter: (to, from, next) => {
      redirectWithPermissionCheck(next, "reports");
    }
  },
  {
    path: "/application-submitted",
    name: "applicationSubmitted",
    component: ApplicationSubmitted,
    // temporary permission
    beforeEnter: (to, from, next) => {
      return redirectWithPermissionCheck(next, "bookings.view");
    }
  },
  {
    path: "/profile-submitted",
    name: "profileSubmitted",
    component: ProfileSubmitted
  },
  ...conversationsRoutes,
  ...bookingsRouter,
  ...interviewsRouter,
  ...settingsRouter,
  ...offersRouter,
  ...applicationsRouter,
  ...profileRouter,
  ...workersRouter,
  ...reportsRouter
];

const checkIfNavigateToCreateProfileForm = path => {
  const createProfileFormRoutes = [
    "/dont-meet-requirements",
    "/profile",
    "/invalid-booking",
    "/switch-account"
  ];
  const isUserStatusIncomplete =
    store.state.auth.userData.status === USER_STATUSES.INCOMPLETE;
  return isUserStatusIncomplete && !includes(createProfileFormRoutes, path);
};

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});
router.beforeEach(async (to, from, next) => {
  const token = checkForNewToken(to.query.token) || getToken();
  if (!token) {
    // return store.commit(SET_AUTH_DIALOG_VISIBILITY, true); Used for Embedded login
    const { token: paramToken } = to.query;
    if (paramToken) {
      localStorage.setItem("token", paramToken);
      delete to.query["token"];
      router.replace({ path: to.path, query: to.query });
    } else {
      window.location.href = getAuthLoginUrl();
      return;
    }
  }
  const hasPermissions = get(store, "state.auth.permissions");
  if (!hasPermissions) {
    await store.dispatch(NS_FETCH_SELF_DETAILS);
  }
  if (checkIfNavigateToCreateProfileForm(to.path)) {
    return next({ name: "profile" });
  }
  if (to.path === "/") {
    return next({ name: getFirstPermissionedRouteName() });
  } else {
    return next();
  }
});

export default router;
